import React from 'react';
import { noop } from '@highlight-ui/utils-commons';

import { SelectProps, SelectOption } from './BaseSelect/types';
import { generateOptionsAndOptionGroups } from './BaseSelect/ListBox/helpers';
import {
  DefaultTrigger,
  InputTrigger,
  MultiInputTrigger,
} from './BaseSelect/Trigger';
import BaseSelect from './BaseSelect/BaseSelect';
import { MIN_OPTIONS_TO_DISPLAY_SELECT_ALL } from './BaseSelect/helpers';

function BackwardCompatibleSelectWithRef<T extends SelectOption>(
  {
    className,
    listClassName,
    closeOnSelect = false,
    disabled = false,
    variant = 'inline',
    selectAllLabel = 'Select all',
    metadata,
    multiple = false,
    onSearchChange = noop,
    onSelect = noop,
    onListVisibilityChange = noop,
    options = [],
    emptyStateLabel = 'Nothing to display',
    renderEmptyState,
    renderTrigger,
    optionType = 'single-line',
    searchable = true,
    searchAutofocus = true,
    searchInitialValue = '',
    searchNoResultsLabel = 'No results for',
    searchPlaceholder = 'Search',
    selectedOptions = [],
    minOptionsToDisplaySearch = 8,
    triggerLabel = '',
    outline = 'default',
    autoFocus = false,
    triggerId,
    creatableOptions,
    isClearable = false,
    highlightedText = '',
    isLoading = false,
    submitOptions,
    enableFlowTriggers = false,
    enableNewSearchInput = false,
    limitTwoRows = true,
    enableSubLabelSearch = false,
    minGroupsToDisplayHeader = 2,
    resetSearchOnClose = false,
    popoverCollisionPadding,
  }: SelectProps<T>,
  ref: React.Ref<HTMLDivElement>,
): JSX.Element {
  const newTrigger = multiple ? (
    <MultiInputTrigger limitTwoRows={limitTwoRows} />
  ) : (
    <InputTrigger />
  );

  const selectTrigger = !enableFlowTriggers
    ? renderTrigger || <DefaultTrigger />
    : newTrigger;

  return (
    <BaseSelect
      ref={ref}
      className={className}
      variant={variant}
      metadata={metadata}
      searchInitialValue={searchInitialValue}
      selectedOptions={selectedOptions}
      minOptionsToDisplaySearch={minOptionsToDisplaySearch}
      isClearable={isClearable}
      isLoading={isLoading ?? false}
      onSearchChange={onSearchChange}
      onSelect={onSelect as SelectProps<SelectOption>['onSelect']}
      onListVisibilityChange={onListVisibilityChange}
      multiple={multiple}
      optionType={optionType || 'single-line'}
      closeOnSelect={closeOnSelect}
      enableSubLabelSearch={enableSubLabelSearch}
      resetSearchOnClose={resetSearchOnClose}
      popoverCollisionPadding={popoverCollisionPadding}
    >
      <BaseSelect.Trigger
        disabled={disabled || false}
        label={triggerLabel || ''}
        outline={outline || 'default'}
        autoFocus={autoFocus || false}
        triggerId={triggerId}
      >
        {selectTrigger as React.ReactNode}
      </BaseSelect.Trigger>
      <BaseSelect.Content className={listClassName} metadata={metadata}>
        {searchable && options.length >= minOptionsToDisplaySearch && (
          <BaseSelect.Content.SearchInput
            searchAutofocus={searchAutofocus ?? true}
            placeholder={searchPlaceholder || 'Search'}
            enableNewSearchInput={enableNewSearchInput}
          />
        )}

        <BaseSelect.Content.DefaultEmptyState
          label={emptyStateLabel || 'Nothing to display'}
          searchNoResultsLabel={searchNoResultsLabel || 'No results for'}
          customComponent={renderEmptyState}
        />

        <BaseSelect.Content.ListBox
          searchable={searchable}
          highlightedText={highlightedText}
          minGroupsToDisplayHeader={minGroupsToDisplayHeader}
        >
          {multiple && options.length > MIN_OPTIONS_TO_DISPLAY_SELECT_ALL && (
            <BaseSelect.Content.ListBox.SelectAllOption
              label={selectAllLabel}
            />
          )}
          {generateOptionsAndOptionGroups(options, minGroupsToDisplayHeader)}
        </BaseSelect.Content.ListBox>

        {!creatableOptions && submitOptions && (
          <BaseSelect.Content.SubmitFooter
            onSubmit={submitOptions.onSubmit}
            onCancel={submitOptions.onCancel}
            submitLabel={submitOptions.submitLabel}
            cancelLabel={submitOptions.cancelLabel}
          />
        )}

        {creatableOptions && (
          <BaseSelect.Content.AddOptionsFooter
            onCreate={creatableOptions.onCreate}
            colorPickerOptions={creatableOptions.colorPickerOptions}
            multiple={multiple}
            createButtonProps={creatableOptions.createButtonProps}
            inputProps={creatableOptions.inputProps}
          />
        )}
      </BaseSelect.Content>
    </BaseSelect>
  );
}

/**
 * According to this article: https://fettblog.eu/typescript-react-generic-forward-refs/
 * we export this value and default because otherwise Storybook won't populate the `ArgTable` correctly
 */
export const BackwardCompatibleSelect = React.forwardRef(
  BackwardCompatibleSelectWithRef,
) as <T extends SelectOption>(
  props: SelectProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof BackwardCompatibleSelectWithRef>;

export default BackwardCompatibleSelect;
